import React from 'react';
import { FiTrendingDown, FiTrendingUp, FiMinus } from 'react-icons/fi';
import { Sparkline } from './Sparkline';
import { cn } from './cn';

const ICON_TONES = {
  brand: 'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300',
  success: 'bg-success-100 text-success-700 dark:bg-success-500/15 dark:text-success-500',
  warning: 'bg-warning-100 text-warning-700 dark:bg-warning-500/15 dark:text-warning-500',
  danger: 'bg-danger-100 text-danger-700 dark:bg-danger-500/15 dark:text-danger-500',
  info: 'bg-info-100 text-info-700 dark:bg-info-500/15 dark:text-info-500',
  neutral: 'bg-ink-100 text-fg-muted dark:bg-ink-700',
};

/**
 * StatTile — KPI card with label, value, optional delta chip, optional sparkline.
 *   delta: number (percentage). When reversed === true (e.g. "Expired"), down is good.
 */
export function StatTile({
  icon,
  label,
  value,
  hint,
  tone = 'brand',
  delta,
  deltaPeriod,
  reversed = false,
  sparkline,
  className,
}) {
  const hasDelta = delta != null && Number.isFinite(delta);
  const isPositive = hasDelta && delta > 0;
  const isNegative = hasDelta && delta < 0;
  const isGood = reversed ? isNegative : isPositive;
  const isBad = reversed ? isPositive : isNegative;

  return (
    <div
      className={cn(
        'flex flex-col justify-between gap-3',
        'rounded-lg border border-line bg-surface p-4 shadow-e1',
        'min-h-[128px]',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-micro text-fg-subtle">{label}</p>
          <p className="mt-1.5 text-display-lg font-bold text-fg tabular-nums">{value}</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {hasDelta && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-caption font-semibold',
                  isGood && 'bg-success-100 text-success-700 dark:bg-success-500/15 dark:text-success-500',
                  isBad && 'bg-danger-100 text-danger-700 dark:bg-danger-500/15 dark:text-danger-500',
                  !isGood && !isBad && 'bg-ink-100 text-fg-muted dark:bg-ink-700',
                )}
              >
                {delta > 0 ? (
                  <FiTrendingUp size={11} />
                ) : delta < 0 ? (
                  <FiTrendingDown size={11} />
                ) : (
                  <FiMinus size={11} />
                )}
                {delta > 0 ? '+' : ''}
                {Math.abs(delta).toFixed(1)}%
              </span>
            )}
            {deltaPeriod && <span className="text-caption text-fg-subtle">{deltaPeriod}</span>}
            {!hasDelta && hint && <span className="text-caption text-fg-subtle">{hint}</span>}
          </div>
        </div>
        {icon && (
          <div
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
              ICON_TONES[tone] || ICON_TONES.brand,
            )}
          >
            {icon}
          </div>
        )}
      </div>

      {sparkline && sparkline.length > 0 && (
        <div className="h-9">
          <Sparkline values={sparkline} tone={tone === 'brand' ? 'brand' : tone} />
        </div>
      )}
    </div>
  );
}

export default StatTile;
